import React from "react";
import BlogPostRow from "./BlogPostRow";

/** Compact signal chip — only rendered when the metric actually exists. */
const Signal = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <span className="text-xs text-gray-500">
      {label} <span className="font-medium text-gray-700">{value}</span>
    </span>
  );
};

/**
 * Refreshed versions of already-published posts, awaiting approval.
 *
 * Listed apart from new drafts because the review question is different: these
 * update a page that already ranks, so what matters is what changed and why —
 * surfaced here as the AI's summary plus the Search Console signals that
 * triggered the refresh.
 */
const RefreshPostsSection = ({ posts, onOpen }) => (
  <>
    <div className="flex items-center justify-between mb-5">
      <div>
        <h2 className="text-xl font-bold text-gray-800">Content Refreshes — Pending Approval</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Rewrites of live posts. Approving updates the existing post in place — the URL never changes.
        </p>
      </div>
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
        posts.length > 0 ? "bg-indigo-100 text-indigo-800" : "bg-gray-100 text-gray-600"
      }`}>
        {posts.length === 0 ? "None waiting" : `${posts.length} to review`}
      </span>
    </div>

    {posts.length === 0 ? (
      <div className="text-center py-10 bg-white rounded-lg shadow-sm border border-gray-200 mb-10">
        <div className="text-4xl mb-3">♻️</div>
        <p className="text-gray-500">No content refreshes pending review.</p>
      </div>
    ) : (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100 mb-10">
        {posts.map(post => {
          const signals    = post.refreshSignals || {};
          const priorTitle = post.refreshOf?.originalTitle;
          // Only worth showing when the refresh actually rewrote the title —
          // otherwise it is noise repeated on every row.
          const titleChanged = priorTitle && priorTitle !== post.title;

          return (
            <BlogPostRow
              key={post.id}
              post={post}
              onClick={() => onOpen(post.id)}
              badge={
                <span className="shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                  ♻️ Refresh
                </span>
              }
              meta={
                <div className="mt-0.5 space-y-0.5">
                  {titleChanged && (
                    <p className="text-xs text-gray-400 truncate">
                      was: <span className="line-through">{priorTitle}</span>
                    </p>
                  )}

                  {post.refreshSummary && (
                    <p className="text-xs text-gray-600 line-clamp-2">{post.refreshSummary}</p>
                  )}

                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-gray-400">{post.date}</span>
                    <Signal label="pos" value={signals.position} />
                    <Signal label="impr" value={signals.impressions} />
                    {(signals.gapQueries || []).length > 0 && (
                      <span className="text-xs text-emerald-600">
                        🎯 {signals.gapQueries.length} gap {signals.gapQueries.length === 1 ? "query" : "queries"}
                      </span>
                    )}
                    {post.status === "regenerating" && (
                      <span className="text-xs text-yellow-600 font-medium">⏳ Regenerating…</span>
                    )}
                  </div>
                </div>
              }
            />
          );
        })}
      </div>
    )}
  </>
);

export default RefreshPostsSection;
